import {
  addDays, format, startOfDay, endOfDay
} from "date-fns"

/**
 * Calculate the number of rows a job service should span based on its estimated hours
 * and the duration of each time period in the schedule grid.
 *
 * @param {number} estimatedHours - The estimated duration of the job service in hours
 * @param {number} periodDurationMinutes - The duration of each time period row in minutes
 * @returns {number} The number of rows the job service should span (minimum 1)
 */
export function calculateRowSpan(estimatedHours, periodDurationMinutes) {
  if (!estimatedHours || estimatedHours <= 0 || !periodDurationMinutes || periodDurationMinutes <= 0) {
    return 1
  }
  const totalMinutes = estimatedHours * 60
  return Math.max(1, Math.ceil(totalMinutes / periodDurationMinutes))
}

/**
 * Group job services by their address for the same day and surveyor.
 * Services at the same address are grouped together with aggregated values.
 *
 * @param {Array} services - Array of job service objects with address, price, and estimatedHours
 * @returns {Array} Array of grouped job service objects
 */
export function groupJobServicesByAddress(services) {
  if (!services || !Array.isArray(services) || services.length === 0) {
    return []
  }

  const groups = new Map()

  services.forEach(service => {
    const key = service.address || ""
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(service)
  })

  return Array.from(groups.values()).map(group => ({
    primaryService: group[0],
    services: group,
    totalValue: group.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0),
    maxEstimatedHours: Math.max(...group.map(s => parseFloat(s.estimatedHours) || 0))
  }))
}

/**
 * Helper function to group only unlocked services by address and time.
 *
 * @param {Array} services - Array of unlocked job service objects
 * @returns {Array} Array of grouped job service objects
 */
function groupUnlockedServices(services) {
  if (!services || !Array.isArray(services) || services.length === 0) {
    return []
  }

  const groups = new Map()

  services.forEach(service => {
    // Use the grouping_key from backend if available, otherwise build it
    // IMPORTANT: Include booking_date in the key to ensure groups on different days are separate
    const bookingDate = service.booking_date || service.bookingDate || ""
    const key = service.grouping_key || `${bookingDate}-${service.address || ""}-${service.booking_time || "09:00"}`
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(service)
  })

  return Array.from(groups.entries()).map(([key, group]) => {
    // Sort services within group by booking_date (for tooltip display)
    const sortedServices = [...group].sort((a, b) => {
      const dateA = a.booking_date || ""
      const dateB = b.booking_date || ""
      return dateA.localeCompare(dateB)
    })

    // Determine if this should actually be treated as a group
    // Only treat as a group if there are multiple services AND they have the same grouping_key
    const shouldBeGrouped = group.length > 1 && group.every(s => {
      const bookingDate = s.booking_date || s.bookingDate || ""
      const serviceKey = s.grouping_key || `${bookingDate}-${s.address || ""}-${s.booking_time || "09:00"}`
      return serviceKey === key
    })

    return {
      groupKey: key,
      primaryService: sortedServices[0],
      services: sortedServices,
      totalValue: group.reduce((sum, s) => sum + (parseFloat(s.total_cost || s.totalCost || s.price) || 0), 0),
      maxEstimatedHours: Math.max(...group.map(s => parseFloat(s.estimated_hours || s.estimatedHours) || 0)),
      isGroup: shouldBeGrouped,
      // Since we only group unlocked services, hasNonDraggable should always be false
      hasNonDraggable: false
    }
  })
}

/**
 * Helper function to create an individual item for a locked service.
 *
 * @param {Object} service - The locked job service object
 * @returns {Object} Individual item object for the locked service
 */
function createIndividualLockedItem(service) {
  const bookingDate = service.booking_date || service.bookingDate || ""
  // Create unique key for locked services to prevent any grouping
  const uniqueKey = `${bookingDate}-${service.address || ""}-${service.booking_time || "09:00"}-locked-${service.id}`

  return {
    groupKey: uniqueKey,
    primaryService: service,
    services: [service],
    totalValue: parseFloat(service.total_cost || service.totalCost || service.price) || 0,
    maxEstimatedHours: parseFloat(service.estimated_hours || service.estimatedHours) || 0,
    isGroup: false,
    hasNonDraggable: true
  }
}

/**
 * Group job services by their address and booking time.
 * Services at the same address and time are grouped together.
 *
 * IMPORTANT: Locked services (hasMultipleAssignments OR hasMultipleBookingDates OR checked in)
 * are excluded from grouping and returned as individual items to prevent
 * navigation issues with the "x more" button.
 *
 * After partial failures in drag operations, jobs that were previously grouped
 * might need to be treated as separate items even if they have the same
 * address and time, to prevent stale grouping behavior.
 *
 * @param {Array} services - Array of job service objects
 * @returns {Array} Array of grouped job service objects with metadata
 */
export function groupJobServicesByAddressAndTime(services) {
  if (!services || !Array.isArray(services) || services.length === 0) {
    return []
  }

  // Step 1: Separate locked and unlocked services
  const lockedServices = services.filter(service => {
    const isCheckedIn = service.checked_in_at || service.checkedInAt
    return service.hasMultipleAssignments || service.hasMultipleBookingDates || isCheckedIn
  })
  const unlockedServices = services.filter(service => {
    const isCheckedIn = service.checked_in_at || service.checkedInAt
    return !service.hasMultipleAssignments && !service.hasMultipleBookingDates && !isCheckedIn
  })

  // Step 2: Group only unlocked services
  const unlockedGroups = groupUnlockedServices(unlockedServices)

  // Step 3: Convert locked services to individual items
  const lockedItems = lockedServices.map(service => createIndividualLockedItem(service))

  // Step 4: Combine and return all items
  return [...unlockedGroups, ...lockedItems]
}

/**
 * Check if a job service is draggable based on business rules.
 * A job service is NOT draggable if:
 * - It has multiple assignments (multiple surveyors)
 * - It has multiple booking dates
 * - It has been checked in (work has started)
 *
 * @param {Object} jobService - The job service object
 * @returns {boolean} True if the job service can be dragged
 */
export function isJobServiceDraggable(jobService) {
  if (!jobService) return false

  // Check if job service has been checked in
  // API returns snake_case: checked_in_at
  const isCheckedIn = jobService.checked_in_at || jobService.checkedInAt

  return !jobService.hasMultipleAssignments && !jobService.hasMultipleBookingDates && !isCheckedIn
}

/**
 * Check if a job service group is draggable.
 * A group is NOT draggable if any service in the group is non-draggable.
 *
 * @param {Object} group - The grouped job service object from groupJobServicesByAddressAndTime
 * @returns {boolean} True if the group can be dragged
 */
export function isGroupDraggable(group) {
  if (!group) return false
  if (group.hasNonDraggable) return false
  return group.services.every(s => isJobServiceDraggable(s))
}

/**
 * Get the day name from a date object.
 *
 * @param {Date} date - The date to get the day name from
 * @returns {string} The lowercase day name (e.g., "monday", "tuesday")
 */
export function getDayName(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return ""
  }
  return format(date, "EEEE").toLowerCase()
}

/**
 * Check if a surveyor is available on a specific date.
 * A surveyor is unavailable if they don't work on that day or have a full-day block out.
 *
 * @param {Object} surveyor - The surveyor object with workingSchedule
 * @param {Date} date - The date to check availability for
 * @param {Array} blockOuts - Array of block out objects with startAt and endAt
 * @returns {boolean} True if the surveyor is available, false otherwise
 */
export function isSurveyorAvailable(surveyor, date, blockOuts) {
  if (!surveyor || !date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false
  }

  const dayName = getDayName(date)
  const workingSchedule = surveyor.workingSchedule || surveyor.working_schedule

  if (!workingSchedule) {
    return false
  }

  const daySchedule = workingSchedule[dayName]

  if (!daySchedule || !daySchedule.works) {
    return false
  }

  // Check for full-day block outs
  if (blockOuts && Array.isArray(blockOuts) && blockOuts.length > 0) {
    const dayStart = startOfDay(date).getTime()
    const dayEnd = endOfDay(date).getTime()

    const hasFullDayBlockOut = blockOuts.some(bo => {
      const blockOutStart = new Date(bo.startAt || bo.start_at).getTime()
      const blockOutEnd = new Date(bo.endAt || bo.end_at).getTime()
      return blockOutStart <= dayStart && blockOutEnd >= dayEnd
    })

    if (hasFullDayBlockOut) {
      return false
    }
  }

  return true
}

/**
 * Get an array of dates for a week starting from the given date.
 * Returns dates for Monday through Saturday (6 days).
 *
 * @param {Date} startDate - The start date of the week (should be a Monday)
 * @returns {Array} Array of Date objects for each day of the week
 */
export function getWeekDays(startDate) {
  if (!startDate || !(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
    return []
  }

  const days = []
  for (let i = 0; i < 6; i += 1) {
    days.push(addDays(startDate, i))
  }
  return days
}

/**
 * Format a date to a string key for use in data structures.
 *
 * @param {Date} date - The date to format
 * @returns {string} The formatted date string (e.g., "2026-01-05")
 */
export function formatDateKey(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return ""
  }
  return format(date, "yyyy-MM-dd")
}

/**
 * Parse a time string to get hours and minutes.
 *
 * @param {string} timeString - Time string in format "HH:mm" or "H:mm AM/PM"
 * @returns {Object} Object with hours and minutes properties
 */
export function parseTimeString(timeString) {
  if (!timeString || typeof timeString !== "string") {
    return { hours: 0, minutes: 0 }
  }

  // Handle 24-hour format (e.g., "08:00", "17:30")
  const match24 = timeString.match(/^(\d{1,2}):(\d{2})$/)
  if (match24) {
    return {
      hours: parseInt(match24[1], 10),
      minutes: parseInt(match24[2], 10)
    }
  }

  // Handle 12-hour format (e.g., "8:00 AM", "5:30 PM")
  const match12 = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (match12) {
    let hours = parseInt(match12[1], 10)
    const minutes = parseInt(match12[2], 10)
    const period = match12[3].toUpperCase()

    if (period === "PM" && hours !== 12) {
      hours += 12
    } else if (period === "AM" && hours === 12) {
      hours = 0
    }

    return { hours, minutes }
  }

  return { hours: 0, minutes: 0 }
}

/**
 * Calculate the time period index for a given time within the schedule grid.
 *
 * @param {Date} dateTime - The date/time to calculate the period for
 * @param {number} startHour - The starting hour of the schedule (e.g., 6 for 6AM)
 * @param {number} periodDurationMinutes - The duration of each period in minutes
 * @returns {number} The zero-based index of the time period
 */
export function getTimePeriodIndex(dateTime, startHour, periodDurationMinutes) {
  if (!dateTime || !(dateTime instanceof Date) || Number.isNaN(dateTime.getTime())) {
    return 0
  }

  const hours = dateTime.getHours()
  const minutes = dateTime.getMinutes()
  const totalMinutesFromStart = (hours - startHour) * 60 + minutes

  if (totalMinutesFromStart < 0) {
    return 0
  }

  return Math.floor(totalMinutesFromStart / periodDurationMinutes)
}

/**
 * Validate that a target date is within the current week view.
 *
 * @param {Date} targetDate - The target date to validate
 * @param {Date} weekStartDate - The start date of the current week (Monday)
 * @param {Date} weekEndDate - The end date of the current week (Saturday)
 * @returns {boolean} True if the target date is within the current week
 */
export function isTargetDateWithinWeek(targetDate, weekStartDate, weekEndDate) {
  if (!targetDate || !(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) {
    return false
  }

  if (!weekStartDate || !(weekStartDate instanceof Date) || Number.isNaN(weekStartDate.getTime())) {
    return false
  }

  if (!weekEndDate || !(weekEndDate instanceof Date) || Number.isNaN(weekEndDate.getTime())) {
    return false
  }

  const targetTime = targetDate.getTime()
  const startTime = startOfDay(weekStartDate).getTime()
  const endTime = endOfDay(weekEndDate).getTime()

  return targetTime >= startTime && targetTime <= endTime
}

/**
 * Validate reschedule data before attempting API calls.
 *
 * @param {Object} jobService - The job service being rescheduled
 * @param {Date} originalDate - The original booking date
 * @param {Date} newDate - The new target date
 * @returns {Object} Validation result with success flag and error message
 */
export function validateRescheduleData(jobService, originalDate, newDate) {
  if (!jobService?.id) {
    return {
      success: false,
      error: "Invalid job service: missing ID"
    }
  }

  if (!originalDate || !(originalDate instanceof Date) || Number.isNaN(originalDate.getTime())) {
    return {
      success: false,
      error: "Invalid original date"
    }
  }

  if (!newDate || !(newDate instanceof Date) || Number.isNaN(newDate.getTime())) {
    return {
      success: false,
      error: "Invalid target date"
    }
  }

  // Check if dates are the same (no-op case)
  if (formatDateKey(originalDate) === formatDateKey(newDate)) {
    return {
      success: false,
      error: "No reschedule needed - same date",
      isNoOp: true
    }
  }

  return { success: true }
}

/**
 * Detect if a drag operation is a no-op (no operation needed).
 *
 * @param {number} sourceSurveyorId - The source surveyor ID
 * @param {number} targetSurveyorId - The target surveyor ID
 * @param {number} originalDayIndex - The original day index (0-5 for Mon-Sat)
 * @param {number} targetDayIndex - The target day index (0-5 for Mon-Sat)
 * @returns {boolean} True if this is a no-op operation
 */
export function isNoOpDragOperation(sourceSurveyorId, targetSurveyorId, originalDayIndex, targetDayIndex) {
  return sourceSurveyorId === targetSurveyorId && originalDayIndex === targetDayIndex
}

/**
 * Validate cross-surveyor with date change operation data.
 * This validates the specific requirements for combined operations.
 *
 * @param {Object} jobService - The job service being moved
 * @param {number} targetSurveyorId - The target surveyor ID
 * @param {Date} originalDate - The original booking date
 * @param {Date} newDate - The new target date
 * @returns {Object} Validation result with success flag and error message
 */
export function validateCrossSurveyorWithDateChangeData(jobService, targetSurveyorId, originalDate, newDate) {
  // First run standard reschedule validation
  const basicValidation = validateRescheduleData(jobService, originalDate, newDate)
  if (!basicValidation.success) {
    return basicValidation
  }

  // Validate target surveyor ID
  if (!targetSurveyorId || typeof targetSurveyorId !== "number" || targetSurveyorId <= 0) {
    return {
      success: false,
      error: "Invalid target surveyor ID"
    }
  }

  // Validate that this is actually a cross-surveyor operation
  const currentSurveyorId = jobService.primary_surveyor_id || jobService.primarySurveyorId
  if (currentSurveyorId === targetSurveyorId) {
    return {
      success: false,
      error: "Target surveyor is the same as current surveyor - use same-surveyor reschedule instead"
    }
  }

  // Validate that the job service supports combined operations
  if (!isJobServiceDraggable(jobService)) {
    return {
      success: false,
      error: "This job service cannot be moved because it has multiple assignments or booking dates"
    }
  }

  return { success: true }
}

/**
 * Get user-friendly error message for invalid drag operations.
 *
 * @param {string} errorType - The type of error
 * @param {Object} context - Additional context for the error
 * @returns {string} User-friendly error message
 */
export function getDragValidationErrorMessage(errorType, context = {}) {
  switch (errorType) {
    case "MISSING_DROP_TARGET":
      return "Invalid drop location. Please drop on a valid day cell."
    case "MISSING_DRAG_DATA":
      return "Unable to process drag operation. Please try again."
    case "MISSING_BOOKING_DATE":
      return "Unable to determine original booking date. This job service cannot be rescheduled."
    case "TARGET_DATE_OUT_OF_WEEK":
      return "Cannot reschedule outside the current week view. Please navigate to the target week first."
    case "INVALID_TARGET_DATE":
      return "Invalid target date. Please drop on a valid day cell."
    case "NO_OP_OPERATION":
      return "Job service is already scheduled for this day and surveyor."
    case "NON_DRAGGABLE_SERVICE":
      return "This job service cannot be moved because it has multiple assignments, multiple booking dates, or work has already started."
    case "CONCURRENT_UPDATE":
      return "Another update is in progress. Please wait and try again."
    case "INVALID_COMBINED_OPERATION":
      return "Cannot perform combined surveyor and date change. Please check the job service and target location."
    case "COMBINED_OPERATION_VALIDATION_FAILED":
      return context.message || "Combined operation validation failed. Please try again."
    case "STALE_DATA":
      return "This job service cannot be moved because it has multiple assignments, multiple booking dates, or work has already started. Please refresh the page to sync the latest data."
    default:
      return context.message || "Invalid drag operation. Please try again."
  }
}

/**
 * Check if a date is a public holiday based on the public holidays data.
 *
 * @param {Date} date - The date to check
 * @param {Object} publicHolidaysByDate - Public holidays data indexed by date (YYYY-MM-DD)
 * @returns {boolean} True if the date is a public holiday
 */
export function isPublicHoliday(date, publicHolidaysByDate) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false
  }

  if (!publicHolidaysByDate || typeof publicHolidaysByDate !== "object") {
    return false
  }

  const dateKey = formatDateKey(date)
  const dayData = publicHolidaysByDate[dateKey]

  return dayData?.is_public_holiday === true
}

/**
 * Get public holiday names for a specific date.
 *
 * @param {Date} date - The date to get holidays for
 * @param {Object} publicHolidaysByDate - Public holidays data indexed by date (YYYY-MM-DD)
 * @returns {Array} Array of holiday names for the date
 */
export function getPublicHolidayNames(date, publicHolidaysByDate) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return []
  }

  if (!publicHolidaysByDate || typeof publicHolidaysByDate !== "object") {
    return []
  }

  const dateKey = formatDateKey(date)
  const dayData = publicHolidaysByDate[dateKey]

  if (!dayData?.holidays || !Array.isArray(dayData.holidays)) {
    return []
  }

  return dayData.holidays.map(holiday => holiday.name).filter(Boolean)
}

/**
 * Calculate available capacity for a surveyor on a specific date.
 * This function calculates:
 * - Total working hours from schedule
 * - Assigned hours from existing job services
 * - Buffer time (0.5 hours per job)
 * - Available capacity (total - assigned - buffer)
 *
 * @param {number} surveyorId - The surveyor ID
 * @param {Date} date - The date to calculate capacity for
 * @param {Object} workingSchedule - The surveyor's working schedule
 * @param {Object} scheduleData - The schedule data with job services
 * @returns {number} Available capacity in hours
 */
export function calculateAvailableCapacity(surveyorId, date, workingSchedule, scheduleData) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return 0
  }

  if (!workingSchedule) {
    return 0
  }

  // Step 1: Calculate working hours for the day
  const dayName = getDayName(date)
  const daySchedule = workingSchedule[dayName]

  if (!daySchedule || !daySchedule.works) {
    return 0
  }

  const startTime = daySchedule.start_time || daySchedule.startTime
  const endTime = daySchedule.end_time || daySchedule.endTime

  if (!startTime || !endTime) {
    return 0
  }

  const startParsed = parseTimeString(startTime)
  const endParsed = parseTimeString(endTime)

  const startDecimal = startParsed.hours + (startParsed.minutes / 60.0)
  const endDecimal = endParsed.hours + (endParsed.minutes / 60.0)

  const totalHours = endDecimal - startDecimal

  if (totalHours <= 0) {
    return 0
  }

  // Step 2: Calculate assigned hours from schedule data
  let assignedHours = 0
  let jobCount = 0

  if (scheduleData && scheduleData.days) {
    const dateKey = formatDateKey(date)
    const dayData = scheduleData.days[dateKey]

    if (dayData && dayData.job_services) {
      assignedHours = dayData.job_services.reduce((sum, js) => {
        const hours = js.estimated_hours || js.estimatedHours || 0
        return sum + parseFloat(hours)
      }, 0)

      jobCount = dayData.job_services.length
    }
  }

  // Step 3: Calculate buffer time (0.5 hours per job)
  const bufferHours = jobCount * 0.5

  // Step 4: Calculate available capacity
  const availableHours = Math.max(0, totalHours - assignedHours - bufferHours)

  return availableHours
}
